import AutocompleteTag from 'components/AutocompleteTag';
import InfoTooltip from 'components/InfoTooltip';
import { Checkbox } from 'components/ui/checkbox';
import { ComboboxChip, ComboboxItem } from 'components/ui/combobox';
import { KeyboardEvent, ReactNode, RefObject } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { isEmptinessOption, SelectOption } from '../../ListTableTypes';

/** The row a multi-select filter puts first once the search is long enough, ticking every row the search left. */
export const SELECT_ALL_OPTION: SelectOption = { label: t('forms.select-all'), value: 'SELECT_ALL' };

export const isSelectAllOption = (option: SelectOption) => option.value === SELECT_ALL_OPTION.value;

/**
 * A pick in a list that pins "Is empty" and "Is not empty": they exclude each other, the values and the exclusion, so
 * picking one keeps it alone and drops the exclusion, and picking a value drops them.
 */
export function pickBesideEmptiness(selected: SelectOption[], next: SelectOption[]) {
    const picked = next.find((option) => !selected.some((current) => current.value === option.value));
    if (picked && isEmptinessOption(picked)) return { options: [picked], dropsExclusion: true };
    return { options: picked ? next.filter((option) => !isEmptinessOption(option)) : next, dropsExclusion: false };
}

/**
 * An option of a multi-select filter: the row is the option, and the checkbox is a picture of whether
 * it is selected, so the list stays open and reads as a checklist. The checkbox takes no focus and no
 * pointer events of its own, and the kit's trailing check mark is hidden since the checkbox says it.
 */
export function CheckboxOptionRow({
    option,
    checked,
    indeterminate,
    className,
    children,
}: {
    option: SelectOption;
    checked: boolean;
    indeterminate?: boolean;
    className?: string;
    children: ReactNode;
}) {
    return (
        <ComboboxItem value={option} className={cn('pr-2 [&>.absolute]:hidden', className)}>
            <Checkbox
                variant="secondary"
                checked={checked}
                indeterminate={indeterminate}
                tabIndex={-1}
                aria-hidden
                className="pointer-events-none"
            />
            {children}
        </ComboboxItem>
    );
}

/**
 * The selected values as tags, cut down to the rows the field has room for while it is not focused,
 * with a "+N" tag for the rest (see `useOptionsInactiveMaxRows`). An invisible copy of every tag is laid
 * out beside them for that hook to measure; it is out of flow, so it adds no row to the field, and it
 * takes the field's inline padding so it wraps where the real tags would.
 *
 * `comboboxChips` wraps each tag in the combobox's own chip, which is what registers it into the list
 * the input's arrow keys walk. The tags shown are always the first ones, so a chip's position is its
 * value's position.
 */
export function CollapsedTags({
    values,
    getLabel,
    onDelete,
    showAll,
    visibleCount,
    hiddenCount,
    measureRef,
    disabled,
    tagClassName,
    comboboxChips = false,
}: {
    values: SelectOption[] | string[];
    getLabel: (value: SelectOption | string) => ReactNode;
    onDelete: (index: number) => void;
    showAll: boolean;
    visibleCount: number;
    hiddenCount: number;
    measureRef: RefObject<HTMLDivElement | null>;
    disabled?: boolean;
    tagClassName?: string;
    comboboxChips?: boolean;
}) {
    const all = values as (SelectOption | string)[];
    const shown = showAll ? all : all.slice(0, visibleCount);
    const overflow = !showAll && hiddenCount > 0;
    const keyOf = (value: SelectOption | string, index: number) =>
        typeof value === 'string' ? `${value}-${index}` : String(value.value);

    return (
        <>
            <div
                ref={measureRef}
                aria-hidden
                className="pointer-events-none invisible absolute inset-x-0 top-0 flex h-0 flex-wrap gap-1.5 overflow-hidden px-[inherit]"
            >
                {all.map((value, index) => (
                    <AutocompleteTag
                        key={`measure-${keyOf(value, index)}`}
                        label={getLabel(value)}
                        onDelete={() => {}}
                        className={tagClassName}
                    />
                ))}
                {overflow && <AutocompleteTag label={`+${hiddenCount}`} />}
            </div>
            {shown.map((value, index) => {
                const tag = (
                    <AutocompleteTag
                        label={getLabel(value)}
                        disabled={disabled}
                        onDelete={() => onDelete(index)}
                        className={tagClassName}
                    />
                );
                return comboboxChips ? (
                    // The chip's own pill styling is dropped because the tag inside already draws one.
                    <ComboboxChip
                        key={keyOf(value, index)}
                        showRemove={false}
                        className="h-auto max-w-full bg-transparent p-0"
                    >
                        {tag}
                    </ComboboxChip>
                ) : (
                    <span key={keyOf(value, index)} className="contents">
                        {tag}
                    </span>
                );
            })}
            {overflow && <AutocompleteTag disabled={disabled} label={`+${hiddenCount}`} />}
        </>
    );
}

/**
 * Backspace and Delete in an empty filter search do not take the last tag out: with the tags cut down
 * to "+N", the one removed could be one the user cannot see.
 */
export function keepTagsOnBackspace(event: KeyboardEvent<HTMLInputElement> & { preventBaseUIHandler?: () => void }) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventBaseUIHandler?.();
    }
}

/** The small checkbox at the end of a filter's label row that turns its selection into an exclusion. */
export function ExcludeToggle({
    checked,
    onChange,
    disabled,
    tooltip,
}: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    /** The (i) after the toggle, a message id. */
    tooltip?: string;
}) {
    const toggle = (
        <label className="ml-auto flex cursor-pointer items-center gap-1 select-none has-disabled:cursor-default has-disabled:opacity-50">
            <Checkbox checked={checked} onCheckedChange={onChange} disabled={disabled} />
            <span className="text-caption text-fg-secondary">{t('filters.exclude-selections')}</span>
        </label>
    );
    if (!tooltip) return toggle;
    return (
        <span className="ml-auto flex items-center gap-1">
            {toggle}
            <InfoTooltip text={tooltip} label={t('filters.exclude-selections')} className="h-4" />
        </span>
    );
}
